/**
 * P-5 (項目定義・計算欄) / P-7 (突合ルール) 共通の AST 編集コンポーネント。
 * バックエンドの DAILY_REPORT_OPS (apps/api/src/lib/request-forms/ast.ts) のうち、
 * 「二項の四則演算 + dr_field_ref/line_sum/literal の葉」のみを対象にした簡易エディタ
 * (ReportViewsSettingsPage.tsx の OperandEditor/decomposeAst と同型パターン)。
 *
 * ⚠️ 設計 §10 #22 のコメントの通り、日報の計算欄・突合式は増分1では入力中プレビューを提供しない
 * (クライアント側 AST 評価ミラーを持たない)。ここは「保存する式を組み立てる」ためだけの UI。
 *
 * 対象外 (if/比較演算子を含む式、3項以上のネスト) の場合は decomposeDrAst が null を返し、
 * 呼び出し側は生 JSON 編集へフォールバックすること (decomposeAst の既存パターンに揃える)。
 */
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { DailyReportConfigField } from '@/lib/daily-report-api';

export const DR_BINARY_OPS = ['add', 'sub', 'mul', 'div'] as const;
export type DrBinaryOp = (typeof DR_BINARY_OPS)[number];
const OP_LABELS: Record<DrBinaryOp, string> = { add: '+ 足す', sub: '− 引く', mul: '× 掛ける', div: '÷ 割る' };

export type DrLeaf =
  | { kind: 'field'; field_definition_id: string }
  | { kind: 'line_sum'; field_definition_id: string }
  | { kind: 'literal'; value: number };

export type DrExpr = DrLeaf | { kind: 'binary'; op: DrBinaryOp; left: DrLeaf; right: DrLeaf };

const buildLeafJson = (leaf: DrLeaf): Record<string, unknown> => {
  if (leaf.kind === 'field') return { op: 'dr_field_ref', field_definition_id: leaf.field_definition_id };
  if (leaf.kind === 'line_sum') return { op: 'line_sum', field_definition_id: leaf.field_definition_id };
  return { op: 'literal', value: leaf.value };
};

export const buildDrAst = (expr: DrExpr): Record<string, unknown> => {
  if (expr.kind === 'binary') return { op: expr.op, left: buildLeafJson(expr.left), right: buildLeafJson(expr.right) };
  return buildLeafJson(expr);
};

const decomposeLeaf = (raw: unknown): DrLeaf | null => {
  const n = raw as { op?: string; field_definition_id?: unknown; value?: unknown };
  if (n?.op === 'dr_field_ref' && typeof n.field_definition_id === 'string') return { kind: 'field', field_definition_id: n.field_definition_id };
  if (n?.op === 'line_sum' && typeof n.field_definition_id === 'string') return { kind: 'line_sum', field_definition_id: n.field_definition_id };
  if (n?.op === 'literal' && typeof n.value === 'number') return { kind: 'literal', value: n.value };
  return null;
};

/** 生 AST (JSON オブジェクト、または null) → DrExpr。対象外の構造 (if/比較/3階層以上) は null。 */
export const decomposeDrAst = (raw: Record<string, unknown> | null | undefined): DrExpr | null => {
  if (!raw) return null;
  const leaf = decomposeLeaf(raw);
  if (leaf) return leaf;
  const n = raw as { op?: string; left?: unknown; right?: unknown };
  if (n.op && (DR_BINARY_OPS as readonly string[]).includes(n.op)) {
    const left = decomposeLeaf(n.left);
    const right = decomposeLeaf(n.right);
    if (left && right) return { kind: 'binary', op: n.op as DrBinaryOp, left, right };
  }
  return null;
};

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
// ⚠️ design-expertレビューCritical是正: ネイティブスピナーを抑制する (D-211で指摘済みのパターン、
// DailyReportFieldsSettingsPage.tsx/DailyReportReconciliationRulesSettingsPage.tsx の
// numberInputCls と同じ規約に揃える)。
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

export function DrLeafEditor({ label, value, onChange, fields, lineItemFields }: {
  label: string; value: DrLeaf; onChange: (v: DrLeaf) => void;
  fields: DailyReportConfigField[]; lineItemFields: DailyReportConfigField[];
}) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-text-sub mb-1">{label}</p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Select value={value.kind} onValueChange={(k) => {
          if (k === 'field') onChange({ kind: 'field', field_definition_id: fields[0]?.field_definition_id ?? '' });
          else if (k === 'line_sum') onChange({ kind: 'line_sum', field_definition_id: lineItemFields[0]?.field_definition_id ?? '' });
          else onChange({ kind: 'literal', value: 0 });
        }}>
          <SelectTrigger className="w-32 shrink-0" aria-label={`${label}: 種類`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="field">項目の値</SelectItem>
            <SelectItem value="line_sum">明細行の合計</SelectItem>
            <SelectItem value="literal">数値</SelectItem>
          </SelectContent>
        </Select>
        {value.kind === 'literal' ? (
          <input type="number" value={value.value} onChange={(e) => onChange({ kind: 'literal', value: Number(e.target.value) })}
            className={`${numberInputCls} min-w-0 flex-1`} aria-label={`${label}: 数値`} />
        ) : (
          <Select value={value.field_definition_id} onValueChange={(fid) => onChange({ ...value, field_definition_id: fid })}>
            <SelectTrigger className="min-w-0 flex-1" aria-label={`${label}: 項目`}><SelectValue placeholder="項目を選択してください" /></SelectTrigger>
            <SelectContent>
              {(value.kind === 'line_sum' ? lineItemFields : fields).map((f) => (
                <SelectItem key={f.field_definition_id} value={f.field_definition_id}>{f.label ?? f.field_code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

/** 「単一項目」または「二項の四則演算」を切り替えて編集する。突合ルールの片辺・計算欄の式に使う。 */
export function DrExprEditor({ expr, onChange, fields, lineItemFields }: {
  expr: DrExpr; onChange: (v: DrExpr) => void;
  fields: DailyReportConfigField[]; lineItemFields: DailyReportConfigField[];
}) {
  const mode = expr.kind === 'binary' ? 'binary' : 'single';
  const singleLeaf: DrLeaf = expr.kind === 'binary' ? expr.left : expr;
  return (
    <div className="space-y-2 rounded border border-surface-border p-3 bg-surface-sunken">
      <div className="flex items-center gap-2">
        <Select value={mode} onValueChange={(m) => {
          if (m === 'single') onChange(singleLeaf);
          else onChange({ kind: 'binary', op: 'add', left: singleLeaf, right: { kind: 'literal', value: 0 } });
        }}>
          <SelectTrigger className="w-40 shrink-0" aria-label="式の形"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="single">単一項目</SelectItem>
            <SelectItem value="binary">2項の計算式</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {expr.kind === 'binary' ? (
        <>
          <DrLeafEditor label="左辺" value={expr.left} onChange={(v) => onChange({ ...expr, left: v })} fields={fields} lineItemFields={lineItemFields} />
          <div>
            <p className="text-xs text-text-sub mb-1">演算子</p>
            <Select value={expr.op} onValueChange={(op) => onChange({ ...expr, op: op as DrBinaryOp })}>
              <SelectTrigger className="w-40" aria-label="演算子"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DR_BINARY_OPS.map((op) => <SelectItem key={op} value={op}>{OP_LABELS[op]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DrLeafEditor label="右辺" value={expr.right} onChange={(v) => onChange({ ...expr, right: v })} fields={fields} lineItemFields={lineItemFields} />
        </>
      ) : (
        <DrLeafEditor label="値" value={expr} onChange={onChange} fields={fields} lineItemFields={lineItemFields} />
      )}
    </div>
  );
}
